import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Calendar, ArrowLeft, Users, TrendingUp, DollarSign, ShoppingBag, 
  Activity, Package, ChevronDown, User, Percent, BarChart3, Receipt,
  Clock, CheckCircle, Hash, ExternalLink
} from 'lucide-react';

const AgentDetails = () => {
  const { agentName, date } = useParams();
  const navigate = useNavigate();
  const [agentData, setAgentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(agentName);
  const [expandedProducts, setExpandedProducts] = useState({});

  useEffect(() => {
    const fetchAgentDetails = async () => {
      try {
        const response = await fetch(
          `https://dailycard.co/api/dashboard/agent/${selectedAgent}/financial-report/?start_date=${date}&end_date=${date}&include_orders=true`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
          }
        );
        const result = await response.json();
        setAgentData(result);
      } catch (error) {
        console.error('Error fetching agent details:', error);
      } finally {
        setLoading(false);
      }
    };

    if (selectedAgent && date) {
      setLoading(true);
      fetchAgentDetails();
    }
  }, [selectedAgent, date]);

  const handleAgentChange = (newAgent) => {
    setSelectedAgent(newAgent);
    // تحديث الـ URL بدون إعادة تحميل الصفحة
    window.history.replaceState(null, '', `/profit-margin/agent/${newAgent}/${date}`);
  };

  const toggleProductOrders = (productId) => {
    setExpandedProducts(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'text-green-400 bg-green-400/10 border-green-400/20';
      case 'pending':
        return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20';
      case 'failed':
        return 'text-red-400 bg-red-400/10 border-red-400/20';
      default:
        return 'text-gray-400 bg-gray-400/10 border-gray-400/20';
    }
  };

  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'مكتمل';
      case 'pending': return 'قيد المعالجة';
      case 'failed': return 'فاشل';
      default: return status || 'غير محدد';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4">
        <div className="flex flex-col items-center justify-center gap-4">
          <div className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
          <p className="text-white text-lg text-center">جاري تحميل بيانات الوكيل...</p>
        </div>
      </div>
    );
  }

  if (!agentData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-white text-lg sm:text-xl mb-4">لم يتم العثور على بيانات لهذا الوكيل</p>
          <button
            onClick={() => navigate(`/profit-margin/`)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl transition-colors"
          >
            العودة للخلف
          </button>
        </div>
      </div>
    );
  }

  const StatCard = ({ title, value, icon: Icon, gradient, textColor, subtitle }) => (
    <div className={`bg-gradient-to-br ${gradient} p-4 sm:p-6 rounded-2xl border border-gray-700/50 shadow-xl`}>
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <div className="bg-white/10 backdrop-blur-sm p-2 sm:p-3 rounded-xl">
          <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${textColor}`} />
        </div>
      </div>
      <div>
        <p className="text-gray-300 text-xs sm:text-sm font-medium mb-1 sm:mb-2">{title}</p>
        <p className="text-lg sm:text-2xl font-bold text-white break-all">{value}</p>
        {subtitle && <p className="text-gray-400 text-xs mt-1">{subtitle}</p>}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" dir="rtl">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-64 sm:w-96 h-64 sm:h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-purple-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 sm:mb-8 gap-4">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
            <button
              onClick={() => navigate(`/profit-margin/`)}
              className="p-2 sm:p-3 bg-gray-800/80 hover:bg-gray-700/80 rounded-xl border border-gray-700/50 transition-all duration-300 group flex-shrink-0"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 group-hover:text-white transition-colors" />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent truncate">
                تفاصيل الوكيل: {selectedAgent}
              </h1>
              <p className="text-gray-400 text-sm sm:text-base lg:text-lg flex items-center gap-2 mt-1 sm:mt-2 flex-wrap">
                <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 flex-shrink-0" />
                <span className="text-xs sm:text-sm lg:text-base">
                  {new Date(date).toLocaleDateString('ar-EG')} - التقرير المالي
                </span>
              </p>
            </div>
          </div>
          
          {/* Agent Selector */}
          <div className="relative w-full sm:w-auto">
            <User className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
            <select
              value={selectedAgent}
              onChange={(e) => handleAgentChange(e.target.value)}
              className="appearance-none bg-gradient-to-r from-gray-800/80 to-gray-700/80 backdrop-blur-sm text-gray-100 px-8 sm:px-10 py-2 sm:py-3 pr-10 sm:pr-12 rounded-xl border border-gray-600/50 focus:border-yellow-500/70 outline-none transition-all duration-300 cursor-pointer hover:border-gray-500/70 shadow-lg w-full sm:min-w-[160px] text-sm sm:text-base"
            >
              {agentData.agents.map((agent) => (
                <option key={agent} value={agent} className="bg-gray-800 text-gray-100">
                  {agent}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none w-4 h-4" />
          </div>
        </div>

        {/* Date Range Info */}
        <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-xl p-3 sm:p-4 mb-6 sm:mb-8 border border-blue-500/30">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0">
            <div className="flex items-center gap-2 sm:gap-3">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400 flex-shrink-0" />
              <span className="text-white font-medium text-sm sm:text-base">
                من {new Date(agentData.date_range.start_date).toLocaleDateString('ar-EG')} 
                إلى {new Date(agentData.date_range.end_date).toLocaleDateString('ar-EG')}
              </span>
            </div>
            <span className="text-blue-400 font-bold text-sm sm:text-base">
              {agentData.date_range.total_days} يوم
            </span>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
          <StatCard
            title="إجمالي الطلبات"
            value={agentData.summary.total_orders}
            icon={ShoppingBag}
            gradient="from-blue-600/20 to-blue-800/20"
            textColor="text-blue-400"
          />
          <StatCard
            title="إجمالي الإيرادات"
            value={`$${parseFloat(agentData.summary.total_revenue).toFixed(2)}`}
            icon={DollarSign}
            gradient="from-green-600/20 to-emerald-800/20"
            textColor="text-green-400"
          />
          <StatCard
            title="إجمالي الربح"
            value={`$${parseFloat(agentData.summary.total_profit).toFixed(2)}`}
            icon={TrendingUp}
            gradient="from-yellow-600/20 to-orange-800/20"
            textColor="text-yellow-400"
          />
          <StatCard
            title="التكلفة الأساسية"
            value={`$${parseFloat(agentData.summary.total_base_cost).toFixed(2)}`}
            icon={BarChart3}
            gradient="from-purple-600/20 to-purple-800/20"
            textColor="text-purple-400"
          />
          <StatCard
            title="متوسط الربح"
            value={`$${parseFloat(agentData.summary.average_profit_per_order).toFixed(2)}`}
            icon={Activity}
            gradient="from-indigo-600/20 to-indigo-800/20"
            textColor="text-indigo-400"
            subtitle="لكل طلب"
          />
          <StatCard
            title="هامش الربح"
            value={`${parseFloat(agentData.summary.profit_margin_percentage).toFixed(2)}%`}
            icon={Percent}
            gradient="from-pink-600/20 to-pink-800/20"
            textColor="text-pink-400"
          />
        </div>

        {/* Products Statistics */}
        <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl">
          <div className="p-4 sm:p-6 border-b border-gray-700/50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl">
                <Package className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">إحصائيات المنتجات</h2>
            </div>
          </div>

          <div className="p-3 sm:p-4 lg:p-6 space-y-6 sm:space-y-8">
            {agentData.products && agentData.products.length > 0 ? (
              agentData.products.map((product) => (
                <div key={product.product_id} className="bg-gradient-to-br from-gray-700/30 to-gray-800/30 rounded-2xl border border-gray-600/30 overflow-hidden">
                  {/* Product Header */}
                  <div className="p-4 sm:p-6 border-b border-gray-600/30">
                    <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl overflow-hidden bg-gray-600/50 flex items-center justify-center flex-shrink-0">
                        {product.product_image ? (
                          <img 
                            src={product.product_image} 
                            alt={product.product_name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <Package className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" style={{display: product.product_image ? 'none' : 'block'}} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-white text-base sm:text-lg lg:text-xl mb-1 truncate">{product.product_name}</h4>
                        <p className="text-gray-400 text-xs sm:text-sm">نوع: {product.product_type} • معرف: #{product.product_id}</p>
                      </div>
                      <div className="text-left flex-shrink-0">
                        <p className="text-lg sm:text-2xl font-bold text-blue-400">{product.orders_count}</p>
                        <p className="text-gray-400 text-xs sm:text-sm">طلب</p>
                      </div>
                    </div>

                    {/* Product Stats Grid */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                      <div className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 p-3 sm:p-4 rounded-xl border border-blue-500/20">
                        <p className="text-xs text-blue-300 mb-1">الكمية المباعة</p>
                        <p className="text-sm sm:text-lg font-bold text-blue-400 break-all">{product.total_quantity_sold.toLocaleString()}</p>
                      </div>
                      <div className="bg-gradient-to-br from-green-600/20 to-emerald-800/20 p-3 sm:p-4 rounded-xl border border-green-500/20">
                        <p className="text-xs text-green-300 mb-1">إجمالي الإيرادات</p>
                        <p className="text-sm sm:text-lg font-bold text-green-400 break-all">${parseFloat(product.total_revenue).toFixed(2)}</p>
                      </div>
                      <div className="bg-gradient-to-br from-yellow-600/20 to-orange-800/20 p-3 sm:p-4 rounded-xl border border-yellow-500/20">
                        <p className="text-xs text-yellow-300 mb-1">إجمالي الربح</p>
                        <p className="text-sm sm:text-lg font-bold text-yellow-400 break-all">${parseFloat(product.total_profit).toFixed(2)}</p>
                      </div>
                      <div className="bg-gradient-to-br from-pink-600/20 to-pink-800/20 p-3 sm:p-4 rounded-xl border border-pink-500/20">
                        <p className="text-xs text-pink-300 mb-1">هامش الربح</p>
                        <p className="text-sm sm:text-lg font-bold text-pink-400">{parseFloat(product.profit_margin_percentage).toFixed(2)}%</p>
                      </div>
                    </div>

                    {/* Show Orders Toggle */}
                    {product.orders && product.orders.length > 0 && (
                      <div className="mt-4 sm:mt-6">
                        <button
                          onClick={() => toggleProductOrders(product.product_id)}
                          className="flex items-center gap-2 bg-gradient-to-r from-indigo-600/20 to-purple-600/20 hover:from-indigo-600/30 hover:to-purple-600/30 text-indigo-300 px-3 sm:px-4 py-2 rounded-xl border border-indigo-500/30 transition-all duration-300 text-sm sm:text-base w-full sm:w-auto justify-center sm:justify-start"
                        >
                          <Receipt className="w-4 h-4" />
                          <span>
                            {expandedProducts[product.product_id] ? 'إخفاء' : 'عرض'} الطلبات 
                            ({product.orders.length})
                          </span>
                          <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${
                            expandedProducts[product.product_id] ? 'rotate-180' : ''
                          }`} />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Orders List */}
                  {expandedProducts[product.product_id] && product.orders && (
                    <div className="p-3 sm:p-4 lg:p-6 bg-gray-900/30">
                      <div className="space-y-3 max-h-80 sm:max-h-96 overflow-y-auto custom-scrollbar">
                        {product.orders.map((order, index) => (
                          <div 
                            key={order.order_id} 
                            className="bg-gradient-to-r from-gray-800/60 to-gray-700/60 p-3 sm:p-4 rounded-xl border border-gray-600/30 hover:border-gray-500/50 transition-all duration-300"
                          >
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-2">
                              <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                                <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs font-bold px-2 py-1 rounded-md flex-shrink-0">
                                  #{index + 1}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-white font-semibold text-xs sm:text-sm flex items-center gap-2">
                                    <Hash className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                    <span className="truncate">{order.transaction_id}</span>
                                  </p>
                                  <p className="text-gray-400 text-xs">
                                    طلب: {order.order_id} • {order.username}
                                  </p>
                                </div>
                              </div>
                              <div className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium border flex-shrink-0 ${getStatusColor(order.status)}`}>
                                {getStatusText(order.status)}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
                              <div className="bg-gray-800 p-2 sm:p-3 rounded-lg">
                                <p className="text-gray-400 text-xs mb-1">الكمية</p>
                                <p className="text-cyan-400 font-bold text-xs sm:text-sm break-all">{order.quantity?.toLocaleString() || 'غير محدد'}</p>
                              </div>
                              <div className="bg-gray-800 p-2 sm:p-3 rounded-lg">
                                <p className="text-gray-400 text-xs mb-1">السعر الإجمالي</p>
                                <p className="text-green-400 font-bold text-xs sm:text-sm break-all">${parseFloat(order.total_price).toFixed(2)}</p>
                              </div>
                              <div className="bg-gray-800 p-2 sm:p-3 rounded-lg">
                                <p className="text-gray-400 text-xs mb-1">هامش الربح</p>
                                <p className="text-yellow-400 font-bold text-xs sm:text-sm break-all">${parseFloat(order.profit_margin).toFixed(2)}</p>
                              </div>
                              <div className="bg-gray-800 p-2 sm:p-3 rounded-lg">
                                <p className="text-gray-400 text-xs mb-1">التكلفة الأساسية</p>
                                <p className="text-purple-400 font-bold text-xs sm:text-sm break-all">${parseFloat(order.base_cost).toFixed(2)}</p>
                              </div>
                            </div>

                            <div className="mt-3 flex flex-row items-center justify-between gap-2">
                              <div className="flex items-center gap-2 text-gray-400 text-xs">
                                <Clock className="w-3 h-3 flex-shrink-0" />
                                <span className="text-xs">{new Date(order.order_date).toLocaleString('ar-EG')}</span>
                              </div>
                              <div className="text-gray-200 text-xs sm:text-sm font-medium">
                                {order.username}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-8 sm:py-12">
                <div className="bg-gray-700/50 w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Package className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
                </div>
                <p className="text-gray-400 text-base sm:text-lg">لا توجد منتجات لهذا الوكيل في هذا التاريخ</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Custom Scrollbar Styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        @media (min-width: 640px) {
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(55, 65, 81, 0.3);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, #3b82f6, #1d4ed8);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, #60a5fa, #3b82f6);
        }
      `}</style>
    </div>
  );
};

export default AgentDetails;